/**
 * Oversikt → Dagen, the day as a list (manager redesign, 2026-10-09). One row per shift or
 * registration, so a person with two shifts has two rows. Two questions are answered apart:
 *
 *   presence  where the person is by the punches: on, on a break, gone home, not in yet, or an
 *             open punch so old it proves nothing (stale);
 *   review    whether a manager has to look: no clock-in once the start has passed, a clock-in
 *             far later than planned, an open punch past the plan, work with no plan.
 *
 * Someone clocked in with no plan is present AND needs a look. A missing clock-in is never called
 * absence: it says the punch is missing. A planned lunch is never a recorded break: only the
 * lunch punches are. Without the rota (time tracking only) every registration has no plan, and
 * that is normal, not a finding.
 */
import type { ApiShift } from '@/api/data';
import { T } from '@/i18n';
import { oslo, osloISO } from '@/rules';
import { fullName } from '@/ui/format';
import { type BreakMode, addDays } from '@timefly/core';

const MIN = 60_000;
const at = (iso: string | null | undefined) => (iso ? Date.parse(iso) : Number.NaN);
const clock = (iso: string | null | undefined) => oslo(iso)?.time ?? '';

/** An open punch this long past the planned end is uncertain, not proof that somebody is working. */
export const STALE_AFTER_MIN = 60;
/** Without a plan there is no end to compare: a punch open this long is uncertain. */
export const STALE_UNPLANNED_H = 12;

export type Presence = 'on' | 'break' | 'stale' | 'done' | 'missing' | 'later' | 'open';
export type Tone = 'ok' | 'warn' | 'neu' | 'act' | 'dgr';

export interface DayRow {
  shift: ApiShift;
  presence: Presence;
  /** the punch was made without a planned shift (the clock's "Stemplet uten plan" type) */
  unplanned: boolean;
  review: boolean;
  /** the status words in the row, and their tone */
  label: string;
  tone: Tone;
  /** one sentence for the detail panel: what is known and what to do */
  explain: string;
  /** minutes of recorded break; null when nobody has clocked in */
  pauseMin: number | null;
}

/** The type the clock gives a punch with no plan: the company's "Stemplet uten plan", or the old shared row 1. */
export const isUnplanned = (s: ApiShift) =>
  /uten plan/i.test(s.shiftType?.name ?? '') ||
  // row 1 by its id: renamed from «Unhandled» to «Standard» on 2026-10-22 (#190)
  (s.shift_type_id === 1 && ['Unhandled', 'Standard'].includes(s.shiftType?.name ?? ''));

/** Recorded break minutes, from the lunch punches only; an open break counts to `now`. */
export function pauseOf(s: ApiShift, now: number): number | null {
  if (!s.punch_in_time) return null;
  if (!s.lunch_in_time) return 0;
  const end = s.lunch_out_time ? at(s.lunch_out_time) : now;
  return Math.max(0, Math.round((end - at(s.lunch_in_time)) / MIN));
}

/**
 * Whether a row shows its «N min registrert pause» line: when a break was recorded, or when the
 * company expects recorded breaks. A company on automatic lunch would otherwise read «0 min» on
 * every row (#90).
 */
export const showsPause = (pauseMin: number | null, mode: BreakMode) =>
  (pauseMin ?? 0) > 0 || mode !== 'auto';

/** Finn ekstra bemanning: the people listed, by a name search, else the first `first` until Vis alle (#94 point 4). */
export function freeShown<U extends { name: string; lastname?: string | null }>(
  free: U[],
  q: string,
  all: boolean,
  first = 8,
): U[] {
  const needle = q.trim().toLowerCase();
  if (needle) return free.filter((u) => fullName(u).toLowerCase().includes(needle));
  return all ? free : free.slice(0, first);
}

export function rowOf(s: ApiShift, now: number, tol: number, rota: boolean): DayRow {
  const unplanned = rota && isUnplanned(s);
  const pauseMin = pauseOf(s, now);
  const base = { shift: s, unplanned, pauseMin };
  if (s.user_id == null)
    return {
      ...base,
      presence: 'open',
      review: false,
      label: T('Ledig vakt'),
      tone: 'warn',
      explain: T('Vakten har ingen ansatt ennå. Finn noen i dagsplanen.'),
    };
  const start = at(s.start_time);
  const end = at(s.end_time);
  // minutes clocked in after the planned start; only a planned shift has a start to be late for
  const lateBy = (iso: string) => (rota && !unplanned ? Math.round((at(iso) - start) / MIN) : 0);
  if (s.punch_in_time && s.punch_out_time) {
    // a late clock-in stays flagged after the clock-out until the hours are approved (the old
    // Dagen kept it on the Krever handling list that long; issue #94 point 5)
    const late = lateBy(s.punch_in_time);
    const why = s.is_approved
      ? null
      : unplanned
        ? T('uten plan')
        : late > tol
          ? T('{n} min sen inn', { n: late })
          : null;
    return {
      ...base,
      presence: 'done',
      review: why != null,
      label: why ? `${T('Avsluttet')} · ${why}` : T('Avsluttet'),
      tone: why ? 'warn' : 'neu',
      explain: unplanned
        ? T('Arbeidet er registrert uten planlagt vakt. Kontroller registreringen i Kontroll.')
        : why
          ? T('Planlagt {start}, stemplet inn {in}. Godkjenn timene eller rett registreringen i Kontroll.', {
              start: clock(s.start_time),
              in: clock(s.punch_in_time),
            })
          : T('Vakten er avsluttet og registrert.'),
    };
  }
  if (s.punch_in_time) {
    const staleAt =
      unplanned || !rota ? at(s.punch_in_time) + STALE_UNPLANNED_H * 3_600_000 : end + STALE_AFTER_MIN * MIN;
    if (now > staleAt)
      return {
        ...base,
        presence: 'stale',
        review: true,
        label: T('Fortsatt inn · avklar'),
        tone: 'warn',
        explain:
          unplanned || !rota
            ? T('Registreringen har stått åpen lenge. Avklar faktisk sluttid.')
            : T('Vakten er over, men registreringen er åpen. Avklar faktisk sluttid.'),
      };
    const onBreak = !!s.lunch_in_time && !s.lunch_out_time;
    const late = lateBy(s.punch_in_time);
    const over = rota && !unplanned && now > end;
    const why = unplanned
      ? T('uten plan')
      : late > tol
        ? T('{n} min sen inn', { n: late })
        : over
          ? T('over planlagt slutt')
          : null;
    return {
      ...base,
      presence: onBreak ? 'break' : 'on',
      review: why != null,
      label: `${onBreak ? T('Pause') : T('På jobb')}${why ? ` · ${why}` : ''}`,
      tone: why ? 'warn' : onBreak ? 'act' : 'ok',
      explain: unplanned
        ? T('Registrert inn {in} uten planlagt vakt. Avklar tilknytningen til arbeidet.', {
            in: clock(s.punch_in_time),
          })
        : late > tol
          ? T('Planlagt {start}, stemplet inn {in}.', {
              start: clock(s.start_time),
              in: clock(s.punch_in_time),
            })
          : over
            ? T('Planlagt slutt {end} er passert, og registreringen er fortsatt åpen.', {
                end: clock(s.end_time),
              })
            : onBreak
              ? T('På registrert pause siden {at}.', { at: clock(s.lunch_in_time) })
              : T('Stemplet inn {in}.', { in: clock(s.punch_in_time) }),
    };
  }
  // nobody clocked in: after the start and the tolerance the punch is missing, which is not absence
  if (s.is_approved)
    return {
      ...base,
      presence: 'done',
      review: false,
      label: T('Godkjent'),
      tone: 'neu',
      explain: T('Timene er godkjent uten stempling.'),
    };
  if (now > start + tol * MIN)
    return {
      ...base,
      presence: 'missing',
      review: true,
      label: T('Mangler innstempling'),
      tone: 'warn',
      explain: T('Planlagt {start}–{end}. Avklar registreringen med medarbeideren.', {
        start: clock(s.start_time),
        end: clock(s.end_time),
      }),
    };
  return {
    ...base,
    presence: 'later',
    review: false,
    label: T('Kommer {start}', { start: clock(s.start_time) }),
    tone: 'neu',
    explain: T('Planlagt {start}–{end}.', { start: clock(s.start_time), end: clock(s.end_time) }),
  };
}

/** What needs a look first, then who is here, then who comes, then what is over. */
const ORDER: Presence[] = ['stale', 'missing', 'on', 'break', 'later', 'open', 'done'];
export function dayRows(shifts: ApiShift[], now: number, tol: number, rota: boolean): DayRow[] {
  return shifts
    .map((s) => rowOf(s, now, tol, rota))
    .sort(
      (a, b) =>
        Number(b.review) - Number(a.review) ||
        ORDER.indexOf(a.presence) - ORDER.indexOf(b.presence) ||
        a.shift.start_time.localeCompare(b.shift.start_time),
    );
}

export type Filter = 'alle' | 'avklar' | 'on' | 'break' | 'later' | 'open' | 'done';
export const FILTERS: [Filter, string][] = [
  ['alle', 'Alle'],
  ['avklar', 'Må avklares'],
  ['on', 'På jobb'],
  ['break', 'Pause'],
  ['later', 'Kommer'],
  ['open', 'Ledige vakter'],
  ['done', 'Avsluttet'],
];
export const matches = (r: DayRow, f: Filter) =>
  f === 'alle' ? true : f === 'avklar' ? r.review : r.presence === f;

/**
 * The header's numbers, each with its unit: people at work (a person on two shifts is one), of
 * them on a recorded break, shifts still to start with the next start, and rows to look at (a
 * shift or registration each). The old Dagen's "Senere i dag · neste 14:00" figure (issue #94).
 */
export function counts(rows: DayRow[]): {
  present: number;
  onBreak: number;
  later: number;
  next: string | null;
  review: number;
} {
  const people = (ps: Presence[]) =>
    new Set(rows.filter((r) => ps.includes(r.presence)).map((r) => r.shift.user_id)).size;
  const later = rows
    .filter((r) => r.presence === 'later')
    .map((r) => r.shift.start_time)
    .sort();
  return {
    present: people(['on', 'break']),
    onBreak: people(['break']),
    later: later.length,
    next: later[0] ? clock(later[0]) : null,
    review: rows.filter((r) => r.review).length,
  };
}

/** "2 aktiviteter gjenstår", the activities on the shift not completed; null when it has none. */
export function tasksLeft(s: ApiShift): number | null {
  const t = s.taskAssignments ?? [];
  return t.length ? t.filter((a) => a.status !== 'completed').length : null;
}

/** The shifts of a day: those starting on it, and those from the day before still running into it. */
export function shiftsOfDay(shifts: ApiShift[], d: string): ApiShift[] {
  const midnight = Date.parse(osloISO(d, '00:00'));
  return shifts.filter((s) => {
    const day = oslo(s.start_time)?.date ?? '';
    if (day === d) return true;
    if (day !== addDays(d, -1)) return false;
    // overnight into this day, or an open punch carried over
    return Date.parse(s.end_time) > midnight || (!!s.punch_in_time && !s.punch_out_time);
  });
}
